import { useEffect, useId, useState } from 'react';

import { Input } from '../../components/ui/Input';
import { Tag } from '../../components/ui/Tag';
import { normalizeStudentKeyword as normalize, searchStudents, type Student } from '../../data/students';
import styles from './Page.module.css';

function Highlight({ text, keyword }: { text: string; keyword: string }) {
  const query = normalize(keyword);
  if (!query) return text;
  // 保留电话的原有空格，同时按去除空格后的关键词定位高亮。
  const characters = Array.from(text);
  const positions = characters.flatMap((character, index) => /\s/.test(character) ? [] : [index]);
  const normalizedText = normalize(text);
  const ranges: { start: number; end: number }[] = [];
  let match = normalizedText.indexOf(query);
  while (match !== -1) {
    ranges.push({ start: positions[match], end: positions[match + query.length - 1] + 1 });
    match = normalizedText.indexOf(query, match + query.length);
  }
  let cursor = 0;
  return <>{ranges.map(({ start, end }) => {
    const before = text.slice(cursor, start);
    cursor = end;
    return <span key={start}>{before}<mark className={styles.searchMatch}>{text.slice(start, end)}</mark></span>;
  })}{text.slice(cursor)}</>;
}

export function ChargeStudentSearch() {
  const id = useId();
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedId, setSelectedId] = useState('');
  const query = normalize(keyword);
  const matches = query ? searchStudents(query) : [];
  const expanded = open && query !== '';
  const activeId = expanded && matches[activeIndex] ? `${id}-${matches[activeIndex].id}` : undefined;

  useEffect(() => {
    if (activeId) document.getElementById(activeId)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeId]);

  function selectStudent(student: Student) {
    setSelectedId(student.id);
    setKeyword(student.name);
    setOpen(false);
  }

  return <div className={styles.studentSearchRoot} onBlur={(event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }}>
    <Input
      allowClear
      aria-activedescendant={activeId}
      aria-autocomplete="list"
      aria-controls={expanded ? `${id}-results` : undefined}
      aria-expanded={expanded}
      aria-haspopup="listbox"
      ariaLabel="搜索收费学员"
      autoComplete="off"
      className={styles.studentSearch}
      leadingIcon="search"
      onFocus={() => { if (query) setOpen(true); }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setOpen(false);
        } else if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && query) {
          event.preventDefault();
          setOpen(true);
          setActiveIndex((index) => !expanded ? (event.key === 'ArrowDown' ? 0 : Math.max(0, matches.length - 1)) : matches.length ? (index + (event.key === 'ArrowDown' ? 1 : -1) + matches.length) % matches.length : 0);
        } else if (event.key === 'Enter' && expanded && matches[activeIndex] && !event.nativeEvent.isComposing) {
          event.preventDefault();
          selectStudent(matches[activeIndex]);
        }
      }}
      onValueChange={(value) => {
        setKeyword(value);
        setSelectedId('');
        setActiveIndex(0);
        setOpen(Boolean(normalize(value)));
      }}
      placeholder="输入姓名、电话、学号"
      role="combobox"
      value={keyword}
    />
    {expanded ? <div className={styles.studentResults}>
      <ul aria-label="匹配学员" className={styles.studentOptions} id={`${id}-results`} role="listbox">
        {matches.map((student, index) => <li
          aria-selected={selectedId === student.id}
          className={styles.studentOption}
          data-active={index === activeIndex || undefined}
          id={`${id}-${student.id}`}
          key={student.id}
          onClick={() => selectStudent(student)}
          onMouseEnter={() => setActiveIndex(index)}
          onMouseDown={(event) => event.preventDefault()}
          role="option"
        >
          <div className={styles.studentOptionHeader}>
            <span><Highlight keyword={keyword} text={student.name} /></span>
            <Tag category={student.status === '休学' ? 'violet' : undefined} size="small" type={student.status === '试听' ? 'warning' : student.status === '退学' ? 'neutral' : 'default'}>{student.status}</Tag>
          </div>
          <div className={styles.studentOptionMeta}>
            <span><Highlight keyword={keyword} text={student.studentNo} /></span>
            <span><Highlight keyword={keyword} text={student.phone} /></span>
          </div>
        </li>)}
      </ul>
      {matches.length === 0 ? <p className={styles.studentSearchEmpty} role="status">未找到匹配学员</p> : null}
    </div> : null}
  </div>;
}
